/**
 * The structural moment — pure gate model (Relationship Detection Assist PRD
 * v2, phase R2).
 *
 * PRD: docs/9-exploration/RELATIONSHIP-DETECTION-ASSIST-PRD.md §4.
 * Canon: docs/1-canon/12-item-relationship-taxonomy-and-mutation-rules.md
 *        §14 (the Nexus/Twins agent spec) and Rule 13 (cluster resolution
 *        gates all other relationship semantics).
 *
 * WHAT WAS MISSING. Detection already runs: the Twins agent proposes cluster
 * identity, the Nexus agent proposes structural relationships, and
 * `poolViolations` names seven anti-patterns over the assembled set. All three
 * render in side panels a session can walk straight past. Nothing ever asked
 * the room "are these actually N legitimate alternatives?" before it started
 * scoring them, and nothing recorded the answer.
 *
 * This module turns those three existing detectors into ONE ordered reckoning
 * with a recorded outcome. It computes; it decides nothing. No writes, no
 * Supabase, no accept/dismiss — those stay the existing RPCs.
 *
 * THREE THINGS THAT ARE NOT COSMETIC:
 *
 * 1. ORDER IS LAW, NOT LAYOUT. Rule 13: "An item's identity must be resolved
 *    before any other relationship type can be evaluated for that item.
 *    Cluster resolution is always Gate 1." So Twins output is presented first,
 *    and any Nexus proposal or structural violation touching an item whose
 *    identity is still unresolved is marked DEFERRED — visible, but not
 *    actionable, because acting on it would be acting on a relationship
 *    between items that may turn out to be one item. Deferral is per item, not
 *    global: unresolved identity for item A does not freeze a finding about B
 *    and C.
 *
 * 2. A BLOCK IS NOT A PROHIBITION. Every gate is resolvable by explicit human
 *    override, and the override is recorded with its reason. Governance, not
 *    bureaucracy — the point is that someone answered, not that the software
 *    won. `readiness` reports which of the two happened, and the record keeps
 *    them distinguishable forever: `clear` (nothing outstanding) is not the
 *    same claim as `overridden` (someone decided anyway), and a report that
 *    blurred them would be worthless as evidence.
 *
 * 3. THE RECORD LINE IS THE DELIVERABLE. "20 entered; 2 duplicates merged; 16
 *    legitimate alternatives ranked" is the sentence the whole phase exists to
 *    produce, so it is computed here, deterministically, from the resolutions —
 *    not assembled ad hoc in a component where it could drift from what was
 *    actually resolved.
 */

// ---------------------------------------------------------------------------
// §1 — Inputs (narrowed rows; this module never fetches)
// ---------------------------------------------------------------------------

/** One `twins_clusters` row (mig 371), narrowed. Twins = cluster identity. */
export interface ClusterProposalRecord {
  id: string;
  /** 'duplicate' — same need said twice; 'variant' — related variations. */
  clusterType: 'duplicate' | 'variant';
  itemIds: string[];
  rationale: string;
  /** 'active' is the only actionable state (canon 12 §14.3 lifecycle). */
  status: 'active' | 'accepted' | 'dismissed';
  proposedByAgent: string;
}

/** One `nexus_relationship_proposals` row (mig 462), narrowed. */
export interface StructuralProposalRecord {
  id: string;
  sourceItemId: string;
  targetItemId: string;
  relationshipType: string;
  relationshipSubtype?: string;
  rationale: string;
  confidence: string;
  status: 'active' | 'accepted' | 'dismissed';
  /** Prod carries at least 'nexus' and 'excursion_return'. */
  proposedByAgent: string;
}

/** A `poolViolations` finding, structurally identical to `PoolViolation`. */
export interface ViolationRecord {
  patternId: string;
  name: string;
  disposition: 'block' | 'warn';
  detail: string;
  itemIds: string[];
}

/** How a person disposed of one finding, keyed by finding id. */
export type ResolutionKind = 'confirmed' | 'dismissed' | 'restructured' | 'overridden';

export interface Resolution {
  kind: ResolutionKind;
  /** Required for `overridden` — an override without a reason is not a record. */
  note?: string;
}

export interface StructuralValidationInput {
  /** Platform item ids actually in the comparison set. */
  poolItemIds: string[];
  clusters: ClusterProposalRecord[];
  proposals: StructuralProposalRecord[];
  violations: ViolationRecord[];
  /** findingId -> how a human disposed of it, so far. */
  resolutions: Record<string, Resolution>;
  /**
   * Which gates actually had a detector looking. Identity and structure come
   * from the Twins and Nexus agents, and a workspace that has never configured
   * or enabled them produces zero proposals — indistinguishable, from the
   * output alone, from a set those agents examined and found nothing wrong
   * with. Comparability is computed client-side and is always checked.
   *
   * Omit for "everything was checked"; existing callers and tests that pass no
   * value keep their current meaning.
   */
  checkedGates?: Partial<Record<GateId, boolean>>;
}

// ---------------------------------------------------------------------------
// §2 — Outputs
// ---------------------------------------------------------------------------

/**
 * The three gates, in Rule 13 order. Gate numbers are canon, not display
 * order: gate 1 must clear before gates 2 and 3 are meaningful *for the items
 * it touches*.
 */
export type GateId = 'identity' | 'structure' | 'comparability';

export const GATE_ORDER: GateId[] = ['identity', 'structure', 'comparability'];

export const GATE_META: Record<GateId, { label: string; source: string; blurb: string }> = {
  identity: {
    label: 'Identity',
    source: 'Twins',
    blurb: 'Are any of these the same thing said twice? Resolved first — Rule 13.',
  },
  structure: {
    label: 'Structure',
    source: 'Nexus',
    blurb: 'Undeclared dependencies, packages and compositions between these items.',
  },
  comparability: {
    label: 'Comparability',
    source: 'Anti-patterns',
    blurb: 'Named ways a set of items cannot legitimately be ranked against each other.',
  },
};

export interface Finding {
  /** Stable id: the source row id, or a derived id for computed violations. */
  id: string;
  gate: GateId;
  /** 'block' findings stop readiness; 'warn' findings do not. */
  disposition: 'block' | 'warn';
  /** Short human label of what was found. */
  detail: string;
  /** Longer agent-supplied reasoning, where there is one. */
  rationale?: string;
  /** Pool items this finding touches (already narrowed to the pool). */
  itemIds: string[];
  /** Who proposed it — an agent name, or 'anti_pattern' for computed ones. */
  source: string;
  /**
   * True when an unresolved identity question covers one of `itemIds`.
   * Deferred findings are shown but not actionable (Rule 13).
   */
  deferred: boolean;
  /** The item ids whose unresolved identity caused the deferral. */
  deferredBehind: string[];
  resolution?: Resolution;
}

export interface GateReport {
  gate: GateId;
  /** False when no detector ran for this gate — findings will be empty and mean nothing. */
  checked: boolean;
  findings: Finding[];
  /** Unresolved, undeferred, blocking. These are what stop readiness. */
  outstandingBlockCount: number;
  outstandingWarnCount: number;
  deferredCount: number;
  resolvedCount: number;
}

export type Readiness =
  /** Nothing outstanding and nothing overridden — the set stands on its own. */
  | 'clear'
  /** Nothing outstanding, but at least one block was cleared by human override. */
  | 'overridden'
  /** Blocking findings remain unresolved. */
  | 'blocked'
  /** Only non-blocking findings remain unresolved. */
  | 'warnings_outstanding'
  /** Detection found nothing to reckon with. */
  | 'nothing_found'
  /**
   * At least one gate had no detector looking, so the set has not been
   * validated — only partly. This is deliberately NOT folded into
   * 'nothing_found': reporting an unrun detector as a clean result is the
   * failure this whole step exists to prevent, and it is the same rule the
   * latency report follows in rendering an unmeasured stage as a labelled gap
   * rather than as a fast one.
   */
  | 'unchecked';

export interface StructuralValidationReport {
  poolSize: number;
  gates: GateReport[];
  readiness: Readiness;
  /**
   * True only when gate 1 has no outstanding blocking finding. While false,
   * gates 2 and 3 are advisory for the items gate 1 touches.
   */
  identityResolved: boolean;
  totals: {
    found: number;
    resolved: number;
    deferred: number;
    outstandingBlocks: number;
    outstandingWarnings: number;
    overrides: number;
  };
  /** Counts by resolution kind — the raw material of the record line. */
  dispositionCounts: Record<ResolutionKind, number>;
  /** "20 entered; 2 duplicates merged; 16 legitimate alternatives ranked." */
  recordLine: string;
}

// ---------------------------------------------------------------------------
// §3 — Finding construction
// ---------------------------------------------------------------------------

/**
 * A cluster is a duplicate question about identity; a `variant` cluster is a
 * softer claim ("closely related variations") and warns rather than blocks.
 * An unresolved DUPLICATE inside one comparison set is the one case where the
 * set is provably invalid — the same bet would be counted twice — so it
 * blocks. This is PRD open question 3, answered: block.
 */
function clusterFindings(
  clusters: ClusterProposalRecord[],
  inPool: (id: string) => boolean,
): Finding[] {
  const findings: Finding[] = [];
  for (const c of clusters) {
    if (c.status !== 'active') continue;
    // Only a cluster with two or more members *inside this set* is a question
    // for this session. One member in the pool and one outside cannot
    // double-count here.
    const ids = c.itemIds.filter(inPool);
    if (ids.length < 2) continue;
    findings.push({
      id: c.id,
      gate: 'identity',
      disposition: c.clusterType === 'duplicate' ? 'block' : 'warn',
      detail:
        c.clusterType === 'duplicate'
          ? `${ids.length} items proposed as the same thing said twice`
          : `${ids.length} items proposed as variations on one theme`,
      rationale: c.rationale,
      itemIds: ids,
      source: c.proposedByAgent,
      deferred: false,
      deferredBehind: [],
    });
  }
  return findings;
}

/**
 * Nexus proposals whose BOTH ends are in the pool. A proposal pointing outside
 * the set is a real finding, but it is not a fact about whether *these* items
 * are comparable with each other, and surfacing it here would bury the ones
 * that are. It stays in the workspace review queue where it already lives.
 */
function proposalFindings(
  proposals: StructuralProposalRecord[],
  inPool: (id: string) => boolean,
): Finding[] {
  const findings: Finding[] = [];
  for (const p of proposals) {
    if (p.status !== 'active') continue;
    if (!inPool(p.sourceItemId) || !inPool(p.targetItemId)) continue;
    const label = p.relationshipSubtype
      ? `${p.relationshipType} (${p.relationshipSubtype})`
      : p.relationshipType;
    findings.push({
      id: p.id,
      gate: 'structure',
      // An undeclared relationship is a candidate, never a prohibition: canon
      // 12 §14.6, "proposals carry no rule weight until accepted". It warns.
      disposition: 'warn',
      detail: `Undeclared ${label} proposed between two items in this set`,
      rationale: p.rationale,
      itemIds: [p.sourceItemId, p.targetItemId],
      source: p.proposedByAgent,
      deferred: false,
      deferredBehind: [],
    });
  }
  return findings;
}

/**
 * `computeViolations` returns no ids of its own, so a violation's identity is
 * derived from what makes it that violation: its pattern and the items it
 * touches. Stable across recomputes (so a resolution sticks while the panel
 * re-renders) and distinct between two findings of the same pattern over
 * different items.
 */
export function violationFindingId(v: Pick<ViolationRecord, 'patternId' | 'itemIds'>): string {
  return `${v.patternId}:${[...v.itemIds].sort().join(',')}`;
}

function violationFindings(violations: ViolationRecord[], inPool: (id: string) => boolean): Finding[] {
  return violations.map((v) => ({
    id: violationFindingId(v),
    gate: 'comparability' as const,
    disposition: v.disposition,
    detail: `${v.name} — ${v.detail}`,
    itemIds: v.itemIds.filter(inPool),
    source: 'anti_pattern',
    deferred: false,
    deferredBehind: [],
  }));
}

// ---------------------------------------------------------------------------
// §4 — The report
// ---------------------------------------------------------------------------

/** A resolution settles a finding; an override settles it *and* is flagged. */
function isResolved(f: Finding): boolean {
  return f.resolution !== undefined;
}

/**
 * Build the ordered gate report for one comparison set.
 *
 * Pure: same inputs, same output, no clock, no client. Everything the panel
 * renders and everything the recorded event carries comes from here, so the
 * sentence in the record and the sentence on the screen cannot disagree.
 */
export function buildStructuralValidation(
  input: StructuralValidationInput,
): StructuralValidationReport {
  const pool = new Set(input.poolItemIds);
  const inPool = (id: string) => pool.has(id);

  const identity = clusterFindings(input.clusters, inPool);
  const structure = proposalFindings(input.proposals, inPool);
  const comparability = violationFindings(input.violations, inPool);

  // Attach resolutions before computing deferral: a cluster question a human
  // has already answered no longer gates anything downstream of it.
  const attach = (f: Finding): Finding => ({ ...f, resolution: input.resolutions[f.id] });
  const identityResolved_ = identity.map(attach);

  // Rule 13 — items still under an unresolved *blocking* identity question.
  // A `variant` cluster warns and does not gate: canon reserves Gate 1's veto
  // for identity that is genuinely unsettled, and treating every soft
  // similarity as a freeze would make the gate meaningless.
  const gatedItems = new Set<string>();
  for (const f of identityResolved_) {
    if (f.disposition !== 'block' || isResolved(f)) continue;
    for (const id of f.itemIds) gatedItems.add(id);
  }

  const deferIfGated = (f: Finding): Finding => {
    const behind = f.itemIds.filter((id) => gatedItems.has(id));
    return { ...f, deferred: behind.length > 0, deferredBehind: behind };
  };

  const byGate: Record<GateId, Finding[]> = {
    identity: identityResolved_,
    structure: structure.map(attach).map(deferIfGated),
    comparability: comparability.map(attach).map(deferIfGated),
  };

  const gates: GateReport[] = GATE_ORDER.map((gate) => {
    const findings = byGate[gate];
    let outstandingBlockCount = 0;
    let outstandingWarnCount = 0;
    let deferredCount = 0;
    let resolvedCount = 0;
    for (const f of findings) {
      if (isResolved(f)) {
        resolvedCount++;
        continue;
      }
      if (f.deferred) {
        // Deferred findings are not outstanding: they are not yet askable.
        // Counting them as outstanding blocks would deadlock the gate — the
        // room cannot answer a question Rule 13 forbids it to answer yet.
        deferredCount++;
        continue;
      }
      if (f.disposition === 'block') outstandingBlockCount++;
      else outstandingWarnCount++;
    }
    const checked = input.checkedGates?.[gate] ?? true;
    return { gate, checked, findings, outstandingBlockCount, outstandingWarnCount, deferredCount, resolvedCount };
  });

  const totals = {
    found: gates.reduce((n, g) => n + g.findings.length, 0),
    resolved: gates.reduce((n, g) => n + g.resolvedCount, 0),
    deferred: gates.reduce((n, g) => n + g.deferredCount, 0),
    outstandingBlocks: gates.reduce((n, g) => n + g.outstandingBlockCount, 0),
    outstandingWarnings: gates.reduce((n, g) => n + g.outstandingWarnCount, 0),
    overrides: 0,
  };

  const dispositionCounts: Record<ResolutionKind, number> = {
    confirmed: 0,
    dismissed: 0,
    restructured: 0,
    overridden: 0,
  };
  for (const g of gates) {
    for (const f of g.findings) {
      if (f.resolution) dispositionCounts[f.resolution.kind]++;
    }
  }
  totals.overrides = dispositionCounts.overridden;

  const identityGate = gates[0];
  const identityIsResolved = identityGate.outstandingBlockCount === 0;

  const uncheckedGates = gates.filter((g) => !g.checked);

  // Order matters: a real outstanding finding outranks an unchecked gate,
  // because something concrete is already known to be wrong. But an unchecked
  // gate outranks every clean verdict — 'clear', 'overridden' and
  // 'nothing_found' all assert that the set was looked at, and one of the
  // gates was not.
  let readiness: Readiness;
  if (totals.outstandingBlocks > 0) readiness = 'blocked';
  else if (totals.outstandingWarnings > 0 || totals.deferred > 0) readiness = 'warnings_outstanding';
  else if (uncheckedGates.length > 0) readiness = 'unchecked';
  else if (totals.found === 0) readiness = 'nothing_found';
  else if (totals.overrides > 0) readiness = 'overridden';
  else readiness = 'clear';

  return {
    poolSize: input.poolItemIds.length,
    gates,
    readiness,
    identityResolved: identityIsResolved,
    totals,
    dispositionCounts,
    recordLine: buildRecordLine(input.poolItemIds.length, gates, dispositionCounts),
  };
}

/**
 * The sales artifact, computed rather than written: what entered, what was
 * done about it, what is left to rank.
 *
 * The final clause is deliberately conservative. It counts items still
 * standing as distinct — pool size minus the items absorbed by a *confirmed*
 * identity cluster (each such cluster collapses n members into 1). A dismissed
 * or overridden cluster absorbs nothing: the room said these are not the same
 * thing, so both still stand and both still get ranked. Claiming otherwise
 * would overstate the cleanup in the direction that flatters the product.
 */
export function buildRecordLine(
  poolSize: number,
  gates: GateReport[],
  counts: Record<ResolutionKind, number>,
): string {
  if (poolSize === 0) return 'No items in the comparison set.';

  const identityGate = gates.find((g) => g.gate === 'identity');
  let absorbed = 0;
  for (const f of identityGate?.findings ?? []) {
    if (f.resolution?.kind === 'confirmed' && f.itemIds.length > 1) {
      absorbed += f.itemIds.length - 1;
    }
  }
  const standing = Math.max(0, poolSize - absorbed);

  const clauses: string[] = [`${poolSize} entered`];
  if (absorbed > 0) clauses.push(`${absorbed} duplicate${absorbed === 1 ? '' : 's'} merged`);
  if (counts.restructured > 0) {
    clauses.push(`${counts.restructured} restructured`);
  }
  if (counts.dismissed > 0) {
    clauses.push(`${counts.dismissed} finding${counts.dismissed === 1 ? '' : 's'} dismissed`);
  }
  if (counts.overridden > 0) {
    clauses.push(`${counts.overridden} overridden`);
  }

  const outstanding = gates.reduce((n, g) => n + g.outstandingBlockCount + g.outstandingWarnCount, 0);
  const found = gates.reduce((n, g) => n + g.findings.length, 0);
  const unchecked = gates.filter((g) => !g.checked);
  if (found === 0 && unchecked.length === 0) {
    clauses.push('no structural findings');
  } else if (outstanding > 0) {
    clauses.push(`${outstanding} unresolved`);
  }
  // This sentence is the artifact the step exists to produce, and it is
  // recorded permanently. It must never let an unrun detector pass as a clean
  // result — the claim "no structural findings" is only available when every
  // gate actually ran.
  if (unchecked.length > 0) {
    clauses.push(
      `${unchecked.map((g) => GATE_META[g.gate].label.toLowerCase()).join(' and ')} not checked`,
    );
  }

  clauses.push(
    `${standing} legitimate alternative${standing === 1 ? '' : 's'} ranked`,
  );
  return `${clauses.join('; ')}.`;
}
